/**
 * The "Pin" control on every book card (ADR-0119; renamed + narrowed from the
 * ADR-0057 "+ Surface" control).
 *
 * Click it to **pin** the matter — a private watch on your Operations Surface's
 * Pinned tab, kept in view for a reason no rule captures. Optional why-note. A
 * pin is always "just me" (private); handing work to the other operator is a
 * Dashboard to-do now, not a Surface action — so there's no assignee here.
 *
 * The composer renders in a **portal to `document.body`** (top layer), not as an
 * absolute panel inside the card. Cards each form their own stacking context, so
 * an in-card panel — however high its z-index — gets painted under sibling cards
 * below it; the portal escapes that entirely and floats above everything. It
 * dismisses on click-outside or Escape.
 *
 * Exported as `SurfaceTrigger` and embedded by the book-card components
 * (props: `leadId`, `className`).
 */

import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useCreateTask } from "@/lib/use-surface-tasks";
import { cn } from "@/lib/utils";

const COMPOSER_WIDTH = 256; // px (w-64)

export function SurfaceTrigger({
  leadId,
  className,
}: {
  leadId: string;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  return (
    <div
      className={cn("relative", className)}
      onClick={(e) => e.stopPropagation()}
    >
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        title="Pin this matter to your Surface"
        data-testid="surface-control"
        className={cn(
          "flex shrink-0 cursor-pointer items-center gap-1 rounded border bg-background/80 px-1.5 py-0.5 text-[11px] font-medium backdrop-blur transition-colors hover:text-foreground",
          open ? "text-foreground ring-1 ring-ring" : "text-muted-foreground",
        )}
      >
        Pin
      </button>
      {open ? (
        <PinComposer
          leadId={leadId}
          anchorRef={triggerRef}
          onClose={() => setOpen(false)}
        />
      ) : null}
    </div>
  );
}

function PinComposer({
  leadId,
  anchorRef,
  onClose,
}: {
  leadId: string;
  anchorRef: React.RefObject<HTMLButtonElement>;
  onClose: () => void;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const [note, setNote] = useState("");
  const create = useCreateTask();

  // Anchor the portal to the trigger, clamped into the viewport. useLayoutEffect
  // so it's placed before paint (no flash at the corner).
  useLayoutEffect(() => {
    const el = anchorRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const left = Math.max(
      8,
      Math.min(r.right - COMPOSER_WIDTH, window.innerWidth - COMPOSER_WIDTH - 8),
    );
    setPos({ top: r.bottom + 4, left });
  }, [anchorRef]);

  // Dismiss on click-outside or Escape.
  useEffect(() => {
    function onPointerDown(e: MouseEvent) {
      const target = e.target as Node;
      if (panelRef.current?.contains(target)) return;
      if (anchorRef.current?.contains(target)) return;
      onClose();
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [anchorRef, onClose]);

  function submit() {
    if (create.isPending) return;
    // A pin is a private watch — no assignee, ever.
    create.mutate(
      {
        kind: "watch" as const,
        leadRecordId: leadId,
        ...(note.trim() ? { note: note.trim() } : {}),
      },
      { onSuccess: onClose },
    );
  }

  return createPortal(
    <div
      ref={panelRef}
      onClick={(e) => e.stopPropagation()}
      style={{
        position: "fixed",
        top: pos?.top ?? -9999,
        left: pos?.left ?? -9999,
        width: COMPOSER_WIDTH,
        zIndex: 100,
      }}
      // bg-popover — the theme defines --popover (docs/rebrand/DECISIONS.md (evicted 2026-08-29, #775 round 10)), so
      // the composer uses its own surface token.
      className="rounded-lg border bg-popover p-2 text-popover-foreground shadow-lg"
      data-testid="surface-composer"
    >
      <textarea
        value={note}
        onChange={(e) => setNote(e.target.value)}
        rows={3}
        placeholder="Why pin this? (optional)"
        className="mb-2 w-full resize-none rounded border bg-background px-2 py-1 text-xs"
        data-testid="surface-composer-text"
      />
      <div className="flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          className="rounded border px-2 py-1 text-xs text-muted-foreground hover:bg-accent hover:text-foreground"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={create.isPending}
          className="rounded bg-primary px-2 py-1 text-xs font-medium text-primary-foreground disabled:opacity-50"
          data-testid="surface-composer-submit"
        >
          Pin
        </button>
      </div>
      {create.isError ? (
        <p className="mt-1 text-[11px] text-error">Couldn't save — try again.</p>
      ) : null}
    </div>,
    document.body,
  );
}
